import { authConfig } from '@/features/auth/config'
import { createLogger } from '@/shared/logging/logger'
import type { AssistantProjectMediaAnalysis } from '@/features/assistant/tools/media-tools'
import { buildDeepSeekPlannerSystemPrompt, buildDeepSeekPlannerUserPrompt } from './planner-prompts'
import { type VoiceoverVideoPlan } from './planner-schema'
import { buildFallbackVoiceoverVideoPlan } from './planner-fallback'
import { validatePlannerResponse } from './planner-validator'

const logger = createLogger('DeepSeekPlanner')
const DEFAULT_MODEL = 'deepseek-v3.1-250821'

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function resolvePlannerEndpoint(): string {
  const configured = import.meta.env.VITE_SUBSITE_AI_TEXT_ENDPOINT?.trim()
  if (configured) {
    return configured
  }

  const apiBaseUrl = authConfig.apiBaseUrl || authConfig.mainSiteUrl
  if (apiBaseUrl) {
    return `${stripTrailingSlash(apiBaseUrl)}/api/subsite/ai/generate`
  }

  return '/api/subsite/ai/generate'
}

function getQianfanModel(): string {
  const configured = import.meta.env.VITE_QIANFAN_MODEL?.trim()
  if (configured) {
    return configured
  }

  return DEFAULT_MODEL
}

interface PlannerApiResponse {
  content?: string
  error?: string
}

export interface DeepSeekPlannerRequest {
  userPrompt: string
  projectMediaAnalysis?: AssistantProjectMediaAnalysis
}

class DeepSeekPlanner {
  isConfigured(): boolean {
    return !!resolvePlannerEndpoint()
  }

  async planVoiceoverVideo({
    userPrompt,
    projectMediaAnalysis,
  }: DeepSeekPlannerRequest): Promise<VoiceoverVideoPlan> {
    const trimmedPrompt = userPrompt.trim()
    if (!trimmedPrompt) {
      throw new Error('Planner prompt cannot be empty.')
    }

    const url = resolvePlannerEndpoint()
    const systemPrompt = buildDeepSeekPlannerSystemPrompt()
    const plannerPrompt = buildDeepSeekPlannerUserPrompt(trimmedPrompt, projectMediaAnalysis)

    logger.info('Requesting planner completion', {
      url,
      model: getQianfanModel(),
      userPromptLength: trimmedPrompt.length,
      mediaContextItems: projectMediaAnalysis?.items.length ?? 0,
    })

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: plannerPrompt,
          systemPrompt,
          model: getQianfanModel(),
          temperature: 0.2,
          maxTokens: 600,
          responseFormat: {
            type: 'json_object',
          },
        }),
      })

      if (!response.ok) {
        let message = `Planner request failed (${response.status}).`

        try {
          const payload = (await response.json()) as PlannerApiResponse
          message = payload.error || message
        } catch {
          // Ignore invalid JSON error bodies.
        }

        throw new Error(message)
      }

      const payload = (await response.json()) as PlannerApiResponse
      const content = payload.content?.trim()
      if (!content) {
        throw new Error('Planner response did not contain message content.')
      }

      const plan = validatePlannerResponse(content)
      logger.info('Planner completion parsed', {
        taskType: plan.taskType,
        durationSeconds: plan.durationSeconds,
        aspectRatio: plan.aspectRatio,
        scriptLength: plan.script.length,
        needsBgm: plan.needsBgm,
      })
      return plan
    } catch (error) {
      logger.warn('Planner request failed, using local fallback plan', {
        message: error instanceof Error ? error.message : String(error),
      })
      return buildFallbackVoiceoverVideoPlan(trimmedPrompt)
    }
  }
}

export const deepseekPlanner = new DeepSeekPlanner()
